import { CONTEXT_MENU_ACTION_CLICKED_EVENT } from '@/consts/analytics.consts';
import { TrackTypeEnum } from '@/hooks/useAnalytics';
import { log } from '@/logger/logger';
import { track } from '@/utils/analytics';
import type { ActionMenuItem, MenuActionKey } from './Actions';

const logger = log.getLogger('MenuAction');

type MenuItemAction = ActionMenuItem['action'];

/** Analytics must not prevent loading cleanup or menu closure. */
function reportCompletion(key: MenuActionKey, succeeded: boolean): void {
  try {
    track(CONTEXT_MENU_ACTION_CLICKED_EVENT, { ...key, succeeded }, TrackTypeEnum.BI);
  } catch (error) {
    logger.error('Failed to track menu action', error);
  }
}

/** Create an action wrapper that reports keyed completion, including after the menu unmounts. */
export function createTrackedMenuAction(item: ActionMenuItem): MenuItemAction {
  const { biEventKey: key, action } = item;
  if (key === undefined) {
    return action;
  }

  return function (event) {
    let result: ReturnType<MenuItemAction>;
    try {
      result = action(event);
    } catch (error) {
      reportCompletion(key, false);
      throw error;
    }

    if (result) {
      return result.then(
        (value) => {
          reportCompletion(key, true);
          return value;
        },
        (error) => {
          reportCompletion(key, false);
          throw error;
        },
      );
    }

    reportCompletion(key, true);
    return result;
  };
}
